import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import BodyText from '../../components/BodyText';
import TopBackNavBar from '../../components/BackNavBar';
import TextInput from '../../components/TextInput';
import { RedButton } from '../../components/Button';
import SelectInput from '../../components/SelectInput';
import validateCreditCardInfofrom, { creditCardInfoErrorsDefault } from './CreditCardValidations';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { CreditCardInfo } from '../../interfaces/CreditCardInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import parseQueryParams from '../../utils/parseQueryParams';
import makeSession from '../../utils/makeSession';
import ExternalLogin from '../../components/ExternalLogin/ExternalLogin';
import { FairBarInfo } from '../../interfaces/fairBar-info';
import FairInfoBar from '../../components/FairInfoBar';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import getFairInfo from '../../utils/getFairInfo';
import { Pages } from '../../App';
import { updateLogInData } from '../../DumbleData';
import { ConfigContext } from '../../providers/ConfigContext';
import getRecaptcha from '../../utils/getRecaptcha';
import getCbsCaptureContext from '../../utils/getCbsCaptureContext';
import BigNumber from 'bignumber.js';

interface Props {
	ccInfo: CreditCardInfo;
	setCCInfo: any;
	pInfo: PaymentInfo;
	payoffAmount: BigNumber;
	setPayoffAmount: React.Dispatch<React.SetStateAction<BigNumber>>;
	version: string;
	setLoggedIn: any;
	loggedIn: boolean;
	payoffFairId: number;
	setPayoffFairId: any;
	payoffFairInfo: FairBarInfo;
	setPayoffFairInfo: any;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const [mySession] = makeSession();
const [getFair] = getFairInfo();
const [getCaptureContext] = getCbsCaptureContext();

const CARD_NUMBER_CONTAINER_ID = 'cc-number-container';
const CARD_CVV_CONTAINER_ID = 'cc-cvv-container';

const AMERICAN_EXPRESS = 'American Express';

const CARD_BRAND_DISPLAY: Record<string, string> = {
	visa: 'Visa',
	mastercard: 'Mastercard',
	amex: AMERICAN_EXPRESS,
	discover: 'Discover',
};

const mapCardBrand = (name?: string) => (name ? (CARD_BRAND_DISPLAY[name.toLowerCase()] ?? '') : '');

// The Microform fields render inside a cross-origin Cybersource iframe, so they can't use our
// self-hosted museo-sans font (@font-face fonts don't cross origins) — fall back to the same
// system-font stack the browser would pick for museo-sans itself, so the rendered text at least
// matches the rest of the page's weight/shape instead of an unstyled browser default.
const flexStyles = {
	input: { 'font-size': '16px', 'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: '#333333' },
	':focus': { color: '#333333' },
	':invalid': { color: '#333333' },
};

const loadSdkScript = (src: string) =>
	new Promise<void>((resolve, reject) => {
		document.querySelector('script[data-flex-microform]')?.remove();
		const script = document.createElement('script');
		script.src = src;
		script.setAttribute('data-flex-microform', 'true');
		script.onload = () => resolve();
		script.onerror = () => reject(new Error(`Failed to load Flex SDK: ${src}`));
		document.head.appendChild(script);
	});

const handleNameChange = (e: any, setter: any, maxChar: number) => {
	const inputValue = e.replace(/[^a-zA-Zà-žÀ-Ž\s'-]/g, '');
	if (inputValue.length <= maxChar) {
		setter(inputValue);
	}
};

const blankError = 'Blank: This field is required.';
const validError = 'Invalid: Please enter a valid value.';
const expiredError = 'Please enter a valid expiration.';
const microformLoadErrorText = 'Unable to load the payment form. Please refresh the page and try again.';

const handleFirstNameChange = (e: any, setFirstName: React.Dispatch<React.SetStateAction<string>>) => {
	handleNameChange(e, setFirstName, 30);
};
const handleLastNameChange = (e: any, setLastName: React.Dispatch<React.SetStateAction<string>>) => {
	handleNameChange(e, setLastName, 30);
};

// Check if payment type is payoff
const checkForPayoffPayment = (setCurrentPage: React.Dispatch<React.SetStateAction<number>>, setPayoffFairId: any, setPayoffAmount: React.Dispatch<React.SetStateAction<BigNumber>>) => {
	const { type, fairId, amount } = parseQueryParams(window.location);

	if (type !== 'payoff' || !fairId || !amount) {
		setCurrentPage(Pages.Error);
		return;
	}

	// Handle case where fairId could be an array or a single value
	const parsedFairId = Array.isArray(fairId) ? parseFloat(fairId[0] || '0') : parseFloat(fairId || '0');
	const parsedAmount = Array.isArray(amount) ? parseFloat(amount[0] || '0') : parseFloat(amount || '0');

	if (isNaN(parsedFairId) || isNaN(parsedAmount)) {
		setCurrentPage(Pages.Error);
		return;
	}

	setPayoffFairId(parsedFairId);
	setPayoffAmount(BigNumber(parsedAmount));
};

const [getCaptcha] = getRecaptcha();

const CreditCardForm = ({
	ccInfo,
	setCCInfo,
	pInfo,
	payoffAmount,
	setPayoffAmount,
	version,
	setLoggedIn,
	loggedIn,
	payoffFairId,
	setPayoffFairId,
	payoffFairInfo,
	setPayoffFairInfo,
	setCurrentPage,
}: Props) => {
	const { apiBase, reCaptchaCheckboxKey, reCaptchaSiteKey } = useContext(ConfigContext);

	const [loading, setLoading] = React.useState(true);
	const [firstName, setFirstName] = React.useState('');
	const [lastName, setLastName] = React.useState('');
	const [expirationDateMonth, setExpirationDateMonth] = React.useState('');
	const [expirationDateYear, setExpirationDateYear] = React.useState('');
	const [creditCardInfoErrors, setCreditCardInfoErrors] = React.useState(creditCardInfoErrorsDefault);
	const [cardFieldErrors, setCardFieldErrors] = useState({ cardNumber: false, cvv: false });
	const [tokenizeError, setTokenizeError] = useState('');
	const [microformReady, setMicroformReady] = useState(false);
	const [microformLoadError, setMicroformLoadError] = useState(false);
	const [detectedCardBrand, setDetectedCardBrand] = useState('');
	const [checkboxActive, setCheckboxActive] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);

	const microformRef = useRef<any>(null);
	const numberValidRef = useRef(false);
	const cvvValidRef = useRef(false);
	const ctxRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const currentDate = new Date();

	const initMicroform = useCallback(async () => {
		setMicroformReady(false);
		setMicroformLoadError(false);
		if (ctxRefreshRef.current) {
			clearTimeout(ctxRefreshRef.current);
		}

		const payoffFairIdFromUrl = version === 'payoff' ? (parseQueryParams(window.location).fairId as string | undefined) : undefined;

		const { data: captureContext, error } = await getCaptureContext(apiBase, payoffFairIdFromUrl);
		if (error || !captureContext) {
			setMicroformLoadError(true);
			return;
		}

		try {
			await loadSdkScript(captureContext.clientLibrary);

			const Flex = (window as any).Flex;
			if (!Flex) {
				throw new Error('Flex Microform SDK did not load');
			}

			const flex = new Flex(captureContext.jwt);
			const microform = flex.microform({ styles: flexStyles });
			microformRef.current = microform;

			const numberField = microform.createField('number', { placeholder: '' });
			const cvvField = microform.createField('securityCode', { placeholder: '' });
			numberField.load(`#${CARD_NUMBER_CONTAINER_ID}`);
			cvvField.load(`#${CARD_CVV_CONTAINER_ID}`);

			numberField.on('change', (d: any) => {
				numberValidRef.current = d.valid === true;
				setDetectedCardBrand(mapCardBrand(d.card?.[0]?.name));
			});
			cvvField.on('change', (d: any) => {
				cvvValidRef.current = d.valid === true;
			});

			if (captureContext.exp) {
				const refreshIn = Math.max(0, captureContext.exp * 1000 - Date.now() - 30_000);
				ctxRefreshRef.current = setTimeout(() => {
					microformRef.current = null;
					initMicroform();
				}, refreshIn);
			}

			setMicroformReady(true);
		} catch (err) {
			setMicroformLoadError(true);
		}
	}, [apiBase, version]);

	useEffect(() => {
		initMicroform();
		return () => {
			if (ctxRefreshRef.current) {
				clearTimeout(ctxRefreshRef.current);
			}
		};
	}, [initMicroform]);

	useEffect(() => {
		try {
			window.grecaptcha.enterprise.render('recaptchaContainer', {
				sitekey: reCaptchaCheckboxKey,
				action: 'CHECKOUT',
				callback: checkboxCallback,
			});
		} catch (error) {
			// reCAPTCHA may throw an error if the container has already been rendered
		}
	}, [loading]);

	const createTokenAndProceed = () => {
		if (!microformRef.current) {
			setTokenizeError('Card could not be validated.');
			setButtonLoading(false);
			return;
		}
		const paddedExpirationMonth = expirationDateMonth.padStart(2, '0');
		microformRef.current.createToken({ expirationMonth: paddedExpirationMonth, expirationYear: expirationDateYear }, (err: any, token: string) => {
			if (err) {
				setTokenizeError(err?.details?.[0]?.message ?? 'Card could not be validated.');
				setButtonLoading(false);
				return;
			}
			const tempCCInfo = {
				...ccInfo,
				firstName: firstName,
				lastName: lastName,
				cardExpirationMonth: paddedExpirationMonth,
				cardExpirationYear: expirationDateYear,
				cardType: detectedCardBrand,
				transientToken: token,
			};
			setCCInfo(tempCCInfo);
			setCurrentPage(Pages.CreditCardReview);
		});
	};

	const checkboxCallback = async (token: string) => {
		const { data } = await getCaptcha(token, apiBase, true);
		if (!data || data.status.toLowerCase() !== 'success') {
			setButtonLoading(false);
			return;
		}
		createTokenAndProceed();
	};

	useEffect(() => {
		if (version === 'sale') {
			setLoading(false);
		} else if (version === 'payoff') {
			checkForPayoffPayment(setCurrentPage, setPayoffFairId, setPayoffAmount);
		}
	}, [setCurrentPage, setPayoffAmount, setPayoffFairId, version]);

	useEffect(() => {
		const getFairInfo = async (fairId: any) => {
			const { data, error } = await getFair(apiBase, fairId);
			if (error) {
				setCurrentPage(Pages.Error);
			} else if (data) {
				setPayoffFairInfo(data);
			}
		};

		// Get session info
		const createSessionInfo = async (fairId: any) => {
			const { data, error } = await mySession(apiBase, fairId);
			if (error) {
				setLoggedIn(!(error.errorMessage && error.errorMessage === 'Not a valid session. Please make sure that a valid SCHL cookie is specified.'));
				updateLogInData();
			} else if (data) {
				setLoggedIn(true);
			}
		};

		if (payoffFairId !== 0) {
			createSessionInfo(payoffFairId).finally(() => {
				getFairInfo(payoffFairId).finally(() => {
					setLoading(false);
				});
			});
		}
	}, [apiBase, payoffFairId, payoffAmount, setCurrentPage, setLoggedIn, setPayoffFairInfo]);

	const handleSubmit = () => {
		setButtonLoading(true);
		setTokenizeError('');
		const errors = validateCreditCardInfofrom({ firstName, lastName, expirationMonth: expirationDateMonth, expirationYear: expirationDateYear });
		setCreditCardInfoErrors(errors);
		const fieldErrors = { cardNumber: !numberValidRef.current, cvv: !cvvValidRef.current };
		setCardFieldErrors(fieldErrors);
		if (Object.values(errors).every((x) => x === false) && !fieldErrors.cardNumber && !fieldErrors.cvv) {
			window._satellite.track('credit-card-review-payment-button');
			window.grecaptcha.enterprise.ready(async () => {
				const token = await window.grecaptcha.enterprise.execute(reCaptchaSiteKey, { action: 'CHECKOUT' });
				const { data } = await getCaptcha(token, apiBase, false);
				if (!data) {
					setButtonLoading(false);
					return;
				}
				if (data.status.toLowerCase() !== 'success') {
					setCheckboxActive(true);
					return;
				}
				createTokenAndProceed();
			});
		} else {
			setButtonLoading(false);
		}
	};

	return (
		<>
			{!loading && (
				<>
					{!loggedIn ? (
						<ExternalLogin fairId={payoffFairId.toString()} setLoggedIn={setLoggedIn} handleRedirect={window.location.href} />
					) : (
						<>
							{version === 'payoff' && (
								<>
									<LandingPageHeader />
									<FairInfoBar fairInfo={payoffFairInfo} />
								</>
							)}
							<TopBackNavBar setCurrentPage={setCurrentPage} title="Credit Card Payment" location={version === 'sale' ? Pages.PaymentMethod : undefined} />
							<CreditCardFormPage>
								<CalculationsDiv>
									{version === 'sale' && (
										<>
											<RowItemDiv>
												<ItemLabel>Item Total</ItemLabel>
												<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
											</RowItemDiv>
											<HorizontalLine />
											<RowItemDiv>
												<ItemLabel>Tax</ItemLabel>
												<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
											</RowItemDiv>
											<HorizontalLine />
											<RowItemDiv>
												<ItemLabel>Amount Due</ItemLabel>
												<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
											</RowItemDiv>
										</>
									)}
									{version === 'payoff' && (
										<>
											<RowItemDiv>
												<ItemLabel>Amount Due</ItemLabel>
												<ItemValue>{formatCurrencyNumber(payoffAmount.toNumber())}</ItemValue>
											</RowItemDiv>
										</>
									)}
								</CalculationsDiv>
								<ContentWrapper>
									<CreditCardInfoDiv>
										<NameDiv>
											<TextInputWrapperBig>
												<TextInput label="First Name" value={firstName} onChange={(e) => handleFirstNameChange(e, setFirstName)} error={creditCardInfoErrors.firstName} errorText={blankError} />
											</TextInputWrapperBig>
											<TextInputWrapperBig>
												<TextInput label="Last Name" value={lastName} onChange={(e) => handleLastNameChange(e, setLastName)} error={creditCardInfoErrors.lastName} errorText={blankError} />
											</TextInputWrapperBig>
										</NameDiv>
										<MicroformFieldContainer error={cardFieldErrors.cardNumber}>
											<MicroformFieldLabel>Credit Card Number</MicroformFieldLabel>
											<MicroformField id={CARD_NUMBER_CONTAINER_ID} data-error={cardFieldErrors.cardNumber || undefined} />
											{cardFieldErrors.cardNumber && <MicroformErrorText>{validError}</MicroformErrorText>}
										</MicroformFieldContainer>
										<LowerCreditCardDiv>
											<TextInputWrapperSmall>
												<SelectInput
													label={'Expiration'}
													optionChosen={expirationDateMonth}
													onChange={setExpirationDateMonth}
													options={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
													defaultText="Month"
													error={creditCardInfoErrors.expirationMonth}
													errorText={blankError}
												/>
											</TextInputWrapperSmall>
											<TextInputWrapperSmall>
												<SelectInput
													label={' '}
													optionChosen={expirationDateYear}
													onChange={setExpirationDateYear}
													options={Array.from({ length: 20 }, (_, i) => (currentDate.getFullYear() + i).toString())}
													defaultText="Year"
													error={creditCardInfoErrors.expirationYear}
													errorText={blankError}
												/>
											</TextInputWrapperSmall>
											<TextInputWrapperSmall>
												<MicroformFieldContainer error={cardFieldErrors.cvv}>
													<MicroformFieldLabel>Security Code</MicroformFieldLabel>
													<MicroformField id={CARD_CVV_CONTAINER_ID} data-error={cardFieldErrors.cvv || undefined} />
													{cardFieldErrors.cvv && <MicroformErrorText>{validError}</MicroformErrorText>}
												</MicroformFieldContainer>
											</TextInputWrapperSmall>
										</LowerCreditCardDiv>
									</CreditCardInfoDiv>
									<RecaptchaContainer $checkboxActive={checkboxActive} className="g-recaptcha" id="recaptchaContainer"></RecaptchaContainer>
									<StyledRedButton handleClick={handleSubmit} disabled={buttonLoading || !microformReady}>
										Review Payment
									</StyledRedButton>
									{creditCardInfoErrors.cardExpired && <ErrorText>{expiredError}</ErrorText>}
									{tokenizeError && <ErrorText>{tokenizeError}</ErrorText>}
									{microformLoadError && <ErrorText>{microformLoadErrorText}</ErrorText>}
								</ContentWrapper>
							</CreditCardFormPage>
						</>
					)}
				</>
			)}
		</>
	);
};

const RecaptchaContainer = styled.div<{ $checkboxActive?: boolean }>`
	margin-top: 40px;
	display: ${(props) => (props.$checkboxActive ? 'block' : 'none')};
`;

const ErrorText = styled.p`
	color: #e81111;
	font-weight: 500;
	font-size: 13px;
	font-height: 15px;
	margin: 0;
`;

const CreditCardFormPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	padding: 0 20px;
	@media (max-width: 480px) {
		width: 100%;
	}
`;

const CalculationsDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	min-width: 280px;
	margin: 32px 0 40px 0;
	gap: 12px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const CreditCardInfoDiv = styled.div`
	display: inline-flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 25px;
	max-width: 440px;
	@media (max-width: 480px) {
		width: 100%;
	}
`;

const NameDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	width: 100%;
	gap: 40px;
	@media (max-width: 480px) {
		flex-direction: column;
		gap: 25px;
	}
`;

const TextInputWrapperBig = styled.div`
	width: 200px;
	@media (max-width: 480px) {
		width: 100%;
	}
`;

const LowerCreditCardDiv = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 30px;
	@media (max-width: 480px) {
		gap: 16px;
	}
`;

const TextInputWrapperSmall = styled.div`
	flex: 1;
	min-width: 120px;
	@media (max-width: 480px) {
		flex: none;
		width: 120px;
		&:nth-child(1),
		&:nth-child(2) {
			width: calc(50% - 8px);
		}
		&:nth-child(3) {
			margin-top: 9px;
			width: 100%;
		}
	}
`;

const StyledRedButton = styled(RedButton)`
	margin: 45px 0 20px 0;
	width: 100%;
`;

const MicroformFieldContainer = styled.div<{ error?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const MicroformFieldLabel = styled.label`
	display: block;
	font-family: museo-sans;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 15px;
	color: #333333;
	padding-bottom: 8px;
`;

const MicroformField = styled.div<{ 'data-error'?: boolean }>`
	height: 40px;
	background: #ffffff;
	border: 1px solid #919191;
	box-sizing: border-box;
	border-radius: 4px;
	padding: 0 16px;
	&[data-error] {
		border: 2px solid #e81111;
	}
`;

const MicroformErrorText = styled.label`
	display: block;
	font-family: museo-sans;
	font-size: 13px;
	line-height: 15px;
	margin-top: 8px;
	color: #e81111;
	padding-bottom: 0;
`;

export default CreditCardForm;
